import React, {
  useEffect, useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import Card from '../../../../components/card/card.component';
import Checkbox from '../../../../components/checkbox/checkbox.component';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Button from '../../../../components/button/button.component';
import ModalButtonBlock from '../../../../components/modal-button-block/modal-button-block.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import Loader from '../../../../components/loader/loader.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import Hint from '../../../../components/hint/hint.component';
import LineBreak from '../../../../components/line-break/line-break.component';
import LeaveConfirmModal from '../../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Form from '../../../../components/form/form.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../../components/form-field-number/form-field-number.component';
import FormFieldPhone from '../../../../components/form-field-phone/form-field-phone.component';
import FormFieldPassword from '../../../../components/form-field-password/form-field-password.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldAddress from '../../../../components/form-field-address/form-field-address.component';
import FormFieldUpload from '../../../../components/form-field-upload/form-field-upload.component';
import TableRowHidden from '../../../../components/table-row-hidden/table-row-hidden.component';
import FieldAddressBlock from '../../../../components/field-address-block/field-address-block.component';
import FieldPhoneBlock from '../../../../components/field-phone-block/field-phone-block.component';
import ImageFileContainer from '../../../../components/image-file-container/image-file-container.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import FormatService from '../../../../services/format-service';
import FormService from '../../../../services/form-service';
import useScreenSmall from '../../../../hooks/use-screen-small';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../../constants/system/file-extensions';
import {
  ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED, ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED, ACCOUNT_USER_TYPE_LIST_TRUSTED,
} from '../../../../constants/entity/account-user-type-lists';
import {
  ACCOUNT_USER_TYPE_ADMIN, ACCOUNT_USER_TYPE_SYSTEM_ADMIN,
} from '../../../../constants/entity/account-user-types';
import { ROUTE_CONTACT } from '../../../../constants/system/routes';
import { CONTACT_VIEW } from '../../../../constants/entity/permissions';
import ACCOUNT_USER_TYPE_GROUP_EXTERNAL from '../../../../constants/entity/account-user-type-groups';
import AssetDefaultImage from '../../../../static/images/asset-default-image.png';
import CompanyDefaultImage from '../../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const SettingsUserModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;
  const ID_CHECKBOX_LINK_CONTACT: string = 'checkbox-link-contact';

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isUserLinked,
    currentUser,
    userPhotoFile,
    selectedAssetTypes,
    userEmailContactData,
    roleList,
    assetTypeList,
    accountUserTypeList,
    dealList,
    portfolioList,
    accessLevelList,
    contactCompanyList,
    saveFormDeps,
    onCreate,
    onCancel,
    onSubmitCreateForm,
    onCloseModal,
    onEmailChange,
    onLinkContact,
    onUserPhotoChange,
    onUserPhotoDelete,
    onAssetTypesChange,
    onPhoneChange,
    onPhoneWorkChange,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onAccountUserTypeChange,
    onAccountUserDealsChange,
    onAccountUserPortfoliosChange,
    onAccountUserDealAccessLevelChange,
    onSendInvitationCheckboxChange,
    onDealSearch,
    onDealSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [isPasswordVisible, setIsPasswordVisible]: [boolean, Function] = useState(false);
  const [isPasswordConfirmVisible, setIsPasswordConfirmVisible]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [accountUserType, setAccountUserType]: [AccountUserType | null, Function] = useState(null);
  const [accountUserTypeGroup, setAccountUserTypeGroup]: [AccountUserTypeGroup | null, Function] = useState(null);
  const [selectedDeals, setSelectedDeals]: [Deal[] | null, Function] = useState<StateValueDefault>(null);
  const [selectedPortfolios, setSelectedPortfolios]: [Portfolio[] | null, Function] = useState<StateValueDefault>(null);
  const [isUserEmailContactVisible, setIsUserEmailContactVisible]: [boolean, Function] = useState(false);
  const [isDealAccessLevelContainerVisible, setIsDealAccessLevelContainerVisible]: [boolean, Function]
    = useState(false);
  const [isUserInvitationView, setIsUserInvitationView]: [boolean, Function] = useState(false);

  const isCurrentUserSystemAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && currentUser.accountUserType === ACCOUNT_USER_TYPE_SYSTEM_ADMIN;
  const userEmailContactId: number = userEmailContactData ? userEmailContactData.id : null;
  const isAccessLevelView: boolean = !!accountUserTypeGroup
    && accountUserTypeGroup === ACCOUNT_USER_TYPE_GROUP_EXTERNAL;
  const isTrustedUserView: boolean = !!accountUserType && ACCOUNT_USER_TYPE_LIST_TRUSTED.includes(accountUserType);
  const filteredAccountUserTypeList: AccountUserType[] = useMemo((): AccountUserType[] => (
    accountUserTypeList && !!accountUserTypeList.length && (
      !isCurrentUserSystemAdmin ? accountUserTypeList.filter((accountUserTypeItem: AccountUserType): boolean => (
        accountUserTypeItem.name !== ACCOUNT_USER_TYPE_ADMIN
      )) : accountUserTypeList
    )
  ), [accountUserTypeList, isCurrentUserSystemAdmin]);

  const dealAccessLevelClickDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isDealAccessLevelContainerVisible,
  ], [isDealAccessLevelContainerVisible]);

  const userPhotoDefaultPreviewUrl: string = useMemo((): string => (
    isUserLinked && userEmailContactData && userEmailContactData.photo
      ? userEmailContactData.photo : PersonDefaultImage
  ), [isUserLinked, userEmailContactData]);

  const userId: number = FormService.FORM_KEY_NEW;
  const formKeyUser: string = FormService.getIndexedFormFieldKey(userId);
  const formPrefix: string = `userListData[${formKeyUser}]`;

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: accessLevelList,
  }), [accessLevelList]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemRoleList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: roleList,
  }), [roleList]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemAccountUserTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: true,
    groupKey: 'group',
    groupItemLabelKey: 'title',
    optionItemValueKey: 'name',
    optionList: filteredAccountUserTypeList,
  }), [filteredAccountUserTypeList]);

  const menuItemDealList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: dealList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
    selectedItemList: selectedDeals,
  }), [dealList, selectedDeals]);

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: portfolioList,
    selectedItemList: selectedPortfolios,
  }), [portfolioList, selectedPortfolios]);

  const onPasswordVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordVisible(value);
  }, []);

  const onPasswordConfirmVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordConfirmVisible(value);
  }, []);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    onSubmitCreateForm(event, (): void => {
      if (onCreate) {
        onCreate(event);
      } else if (onCloseModal) {
        onCloseModal();
      }

      setAccountUserType(null);
      setSelectedPortfolios(null);
      setSelectedDeals(null);
    });
  }, [isSaveDisabled, ...(saveFormDeps || [])]);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }

    setAccountUserType(null);
    setSelectedPortfolios(null);
    setSelectedDeals(null);
  }, []);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(userId, assetTypeValues);
    }
  }, [userId]);

  const userPhotoChange: Function = useCallback((file: File): void => {
    if (onUserPhotoChange) {
      onUserPhotoChange(userId, file);
    }
  }, [userId]);

  const userPhotoDelete: Function = useCallback((): void => {
    if (onUserPhotoDelete) {
      onUserPhotoDelete(userId);
    }
  }, [userId]);

  const phoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (onPhoneChange) {
      onPhoneChange(userId, phoneValue);
    }
  }, [userId]);

  const phoneWorkChange: PhoneChangeHandlerFunction = useCallback((phoneWorkValue?: string): void => {
    if (onPhoneWorkChange) {
      onPhoneWorkChange(userId, phoneWorkValue);
    }
  }, [userId]);

  const emailChange: Function = useCallback((emailValue: string): void => {
    const emailFilteredValue: string | null = emailValue ? emailValue.trim() : null;

    if (onEmailChange && emailFilteredValue && FormatService.isValidEmail(emailFilteredValue)) {
      onEmailChange(emailFilteredValue);
    }
  }, []);

  const sendInvitationCheckboxChange: CheckboxChangeHandlerFunction = useCallback((
    event?: React.ChangeEvent<HTMLInputElement>,
    isUserInvitationViewValue?: boolean,
  ): void => {
    setIsUserInvitationView(isUserInvitationViewValue);

    if (onSendInvitationCheckboxChange && isUserInvitationViewValue) {
      onSendInvitationCheckboxChange(userId);
    }
  }, [userId]);

  const linkContact: CheckboxChangeHandlerFunction = useCallback((
    event?: React.ChangeEvent<HTMLInputElement>,
    isLinked?: boolean,
  ): void => {
    if (onLinkContact) {
      onLinkContact(userId, isLinked, userEmailContactId || null);
    }
  }, [userId, userEmailContactId]);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled) {
      setIsLeaveConfirmModalOpen(true);
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const accountUserTypeChange: Function = useCallback((accountUserTypeName: string): void => {
    const accountUserTypeListItem: AccountUserType = accountUserTypeList.find((
      accountUserTypeItem: AccountUserType,
    ): boolean => (
      accountUserTypeItem.name === accountUserTypeName
    ));
    const accountUserTypeGroupName: string | null = accountUserTypeListItem.group
      ? accountUserTypeListItem.group.name : null;

    setAccountUserType(accountUserTypeName);
    setAccountUserTypeGroup(accountUserTypeGroupName);

    if (!ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED.includes(accountUserTypeName)) {
      setSelectedDeals(null);
    }

    if (!ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED.includes(accountUserTypeName)) {
      setSelectedPortfolios(null);
    }

    if (onAccountUserTypeChange) {
      onAccountUserTypeChange(userId, accountUserTypeName, accountUserTypeGroupName);
    }
  }, [userId, accountUserTypeList]);

  const accountUserDealsChange: Function = useCallback((dealValues: Deal[]): void => {
    const removedDealValues: Deal[] = (selectedDeals || []).filter((dealValue: Deal): boolean => (
      !dealValues.find((newDealValue: Deal): boolean => newDealValue.id === dealValue.id)
    ));

    if (onAccountUserDealsChange) {
      onAccountUserDealsChange(userId, dealValues, removedDealValues);
    }

    setSelectedDeals(dealValues);

    if (!isDealAccessLevelContainerVisible) {
      setIsDealAccessLevelContainerVisible(true);
    }
  }, [userId, selectedDeals, isDealAccessLevelContainerVisible]);

  const accountUserPortfoliosChange: Function = useCallback((portfolioValues: Portfolio[]): void => {
    if (onAccountUserPortfoliosChange) {
      onAccountUserPortfoliosChange(userId, portfolioValues);
    }

    setSelectedPortfolios(portfolioValues);
  }, [userId]);

  const accountUserDealAccessLevelChange: Function = useCallback((
    accessLevelName: string, dealId: number,
  ): void => {
    if (onAccountUserDealAccessLevelChange) {
      onAccountUserDealAccessLevelChange(userId, dealId, accessLevelName);
    }
  }, [userId]);

  const switchDealAccessLevelContainerVisibility: Function = useCallback((): void => {
    setIsDealAccessLevelContainerVisible(!isDealAccessLevelContainerVisible);
  }, [isDealAccessLevelContainerVisible]);

  useEffect((): void => {
    setIsUserEmailContactVisible(!!userEmailContactId);
  }, [userEmailContactId]);

  return (
    <ModalLayout
      isOpen={isOpen}
      title="New User"
      onCloseModal={cancelChangesAndCloseModal}
      onCloseModalByClickAway={closeModalByClickAway}
    >
      <Form>
        <Card isContainer withNoPadding>
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              <TableRow>
                <TableCell>
                  Photo
                </TableCell>
                <TableCell>
                  <ImageFileContainer withSubBlock>
                    <FormFieldUpload
                      isImage
                      isDroppable
                      isSmallImage
                      isDisabled={isUserLinked && userEmailContactData && !!userEmailContactData.photo}
                      name={`${formPrefix}.photo`}
                      uploadFile={userPhotoFile}
                      buttonLabel="New User"
                      defaultPreviewUrl={userPhotoDefaultPreviewUrl}
                      acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                      onFileChange={userPhotoChange}
                      onFileDelete={userPhotoDelete}
                    />
                  </ImageFileContainer>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  First Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.firstName`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Last Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.lastName`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Company
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withSearch
                    withBlankElement
                    isDisabled={contactCompanyList && !contactCompanyList.length}
                    name={`${formPrefix}.companyId`}
                    optionsTotal={contactCompanyList && contactCompanyList.length}
                    onSearch={onContactCompanySearch}
                    onClose={onContactCompanySearchClose}
                  >
                    {menuItemContactCompanyList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Email
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    withNoAutocomplete
                    name={`${formPrefix}.email`}
                    onChange={(emailValue: string): void => {
                      emailChange(emailValue);
                    }}
                  />
                </TableCell>
              </TableRow>
              {
                isUserEmailContactVisible && (
                  <TableRow>
                    <TableCell>
                      Linked Contact
                    </TableCell>
                    <TableCell>
                      We&apos;ve found not linked contact
                      {
                        userEmailContactData && (
                          <Fragment>
                            {' '}
                            (
                            <FilteredLink
                              withNewWindow
                              link={`${ROUTE_CONTACT}/${userEmailContactData.id}`}
                              permission={CONTACT_VIEW}
                            >
                              {userEmailContactData.fullName}
                            </FilteredLink>
                            )
                          </Fragment>
                        )
                      }
                      {' '}
                      with the same email, you can link it.
                      <LineBreak />
                      Link it to new user
                      <Checkbox withOffset onChange={linkContact} id={ID_CHECKBOX_LINK_CONTACT} />
                    </TableCell>
                  </TableRow>
                )
              }
              <TableRow>
                <TableCell>
                  Phone
                </TableCell>
                <TableCell>
                  <FieldPhoneBlock>
                    <FormFieldPhone isBordered name={`${formPrefix}.phone`} onChange={phoneChange} />
                    <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.phoneExtension`} placeholder="Ext" />
                  </FieldPhoneBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Work Phone
                </TableCell>
                <TableCell>
                  <FieldPhoneBlock>
                    <FormFieldPhone isBordered name={`${formPrefix}.phoneWork`} onChange={phoneWorkChange} />
                    <FormFieldNumber
                      isBordered
                      withNoCommas
                      name={`${formPrefix}.phoneWorkExtension`}
                      placeholder="Ext"
                    />
                  </FieldPhoneBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Address
                </TableCell>
                <TableCell>
                  <FieldAddressBlock>
                    <FormFieldAddress
                      isModal
                      isBordered
                      itemId={FormService.FORM_KEY_NEW}
                      name={`${formPrefix}.streetAddress`}
                      onAddressSearchCompleted={(addressObject: JsonObject): void => {
                        onAddressSearchCompleted(addressObject, userId);
                      }}
                      onKeyDown={(): void => {
                        onAddressFieldKeyDown(userId);
                      }}
                    />
                    <FormFieldText isDisabled isBordered name={`${formPrefix}.location`} />
                    <FormFieldText isDisabled isBordered name={`${formPrefix}.postalCode`} />
                    <FormFieldText isHidden name={`${formPrefix}.address`} />
                  </FieldAddressBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Address Details
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    isMultiline
                    name={`${formPrefix}.addressDetails`}
                    maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
                    minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Role
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    isDisabled={roleList && !roleList.length}
                    name={`${formPrefix}.role`}
                    optionsTotal={roleList && roleList.length}
                  >
                    {menuItemRoleList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Title
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.title`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Asset Focus
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    isDisabled={assetTypeList && !assetTypeList.length}
                    name={`${formPrefix}.assetTypes`}
                    optionList={assetTypeList}
                    onValuesChange={assetTypesChange}
                  >
                    {menuItemAssetTypeList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Bio
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    isMultiline
                    name={`${formPrefix}.bio`}
                    maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                    minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Type
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    isDisabled={filteredAccountUserTypeList && !filteredAccountUserTypeList.length}
                    name={`${formPrefix}.accountUserType`}
                    optionsTotal={filteredAccountUserTypeList && filteredAccountUserTypeList.length}
                    onChange={((accountUserTypeName: string): void => {
                      accountUserTypeChange(accountUserTypeName);
                    })}
                  >
                    {menuItemAccountUserTypeList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              {
                accountUserType && ((selectedPortfolios && !selectedPortfolios.length) || !selectedPortfolios)
                && ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED.includes(accountUserType) && (
                  <TableRow>
                    <TableCell>
                      Allowed Deals
                    </TableCell>
                    <TableCell>
                      <FormFieldMultiSelect
                        isBordered
                        withImage
                        withSearch
                        isDisabled={dealList && !dealList.length}
                        blankElementLabel={isTrustedUserView ? 'All' : 'None'}
                        imageThumbnailKey="mainPhotoThumbnail"
                        imageKey="mainPhoto"
                        name={`${formPrefix}.accountUserDeals`}
                        defaultImage={AssetDefaultImage}
                        optionList={dealList}
                        onSearch={onDealSearch}
                        onClose={onDealSearchClose}
                        onValuesChange={accountUserDealsChange}
                      >
                        {menuItemDealList}
                      </FormFieldMultiSelect>
                    </TableCell>
                  </TableRow>
                )
              }
              {
                accountUserType && ACCOUNT_USER_TYPE_LIST_PORTFOLIO_LIMITED.includes(accountUserType)
                && ((selectedDeals && !selectedDeals.length) || !selectedDeals) && (
                  <TableRow>
                    <TableCell>
                      Allowed Portfolios
                    </TableCell>
                    <TableCell>
                      <FormFieldMultiSelect
                        isBordered
                        isDisabled={portfolioList && !portfolioList.length}
                        blankElementLabel={isTrustedUserView ? 'All' : 'None'}
                        name={`${formPrefix}.accountUserPortfolios`}
                        optionList={portfolioList}
                        onValuesChange={accountUserPortfoliosChange}
                      >
                        {menuItemPortfolioList}
                      </FormFieldMultiSelect>
                    </TableCell>
                  </TableRow>
                )
              }
              {
                selectedDeals && !!selectedDeals.length && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS_TOTAL}>
                      <FilteredLink
                        isSwitcher
                        withRequiredMark={isAccessLevelView && !isDealAccessLevelContainerVisible}
                        clickDeps={dealAccessLevelClickDeps}
                        onClick={switchDealAccessLevelContainerVisibility}
                      >
                        Deal Access Levels
                      </FilteredLink>
                    </TableCell>
                  </TableRow>
                )
              }
              {
                selectedDeals && !!selectedDeals.length && selectedDeals.map((dealItem: Deal): ReactElement => {
                  const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealItem.id);

                  return (
                    <TableRowHidden isActive={isDealAccessLevelContainerVisible} key={dealItem.id}>
                      <TableCell>
                        {`${dealItem.name} Access Level`}
                        {
                          isAccessLevelView && (
                            <RequiredMark />
                          )
                        }
                      </TableCell>
                      <TableCell>
                        <FormFieldSelect
                          isBordered
                          withBlankElement
                          isDisabled={accessLevelList && !accessLevelList.length}
                          name={`${formPrefix}.accountUserDealAccessLevels[${formKeyDeal}]`}
                          optionsTotal={accessLevelList && accessLevelList.length}
                          onChange={((accessLevelName: string): void => {
                            accountUserDealAccessLevelChange(accessLevelName, dealItem.id);
                          })}
                        >
                          {menuItemAccessLevelList}
                        </FormFieldSelect>
                      </TableCell>
                    </TableRowHidden>
                  );
                })
              }
              <TableRow>
                <TableCell>
                  Send Invitation
                  <Hint
                    title={'By choosing this option an inactive user will be created '
                      + 'with a message sent to specified email. User needs to follow the link from email '
                      + 'and fill out register form.'}
                  />
                </TableCell>
                <TableCell>
                  <FormFieldCheckbox
                    withNoOffset
                    withNoLabelOffset
                    name={`${formPrefix}.withSendInvitation`}
                    onChange={sendInvitationCheckboxChange}
                  />
                </TableCell>
              </TableRow>
              {
                isUserInvitationView && (
                  <TableRow>
                    <TableCell>
                      Additional Invitation Message
                    </TableCell>
                    <TableCell>
                      <FormFieldText
                        isBordered
                        isMultiline
                        name={`${formPrefix}.invitationMessage`}
                        minRows={FormService.TEXTAREA_MIN_ROWS_SMALL}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isUserInvitationView && (
                  <TableRow>
                    <TableCell>
                      Password
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldPassword
                        isBordered
                        withStrengthBar
                        withNoAutocomplete
                        isVisible={isPasswordVisible}
                        name={`${formPrefix}.password`}
                        onVisibilityChange={onPasswordVisibilityChange}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isUserInvitationView && (
                  <TableRow>
                    <TableCell>
                      Confirm Password
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldPassword
                        isBordered
                        withNoAutocomplete
                        isVisible={isPasswordConfirmVisible}
                        name={`${formPrefix}.passwordConfirm`}
                        onVisibilityChange={(value: boolean): void => {
                          onPasswordConfirmVisibilityChange(value);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        </Card>
        <ModalButtonBlock isFixed>
          <Button onClick={cancelChangesAndCloseModal}>
            Cancel
          </Button>
          <Button isActive isDisabled={isSaving} onClick={applyChangesAndCloseModal}>
            Save
          </Button>
          {
            isSaving && !isSmallScreen && (
              <Loader isInline isModal isFloating isSmall />
            )
          }
        </ModalButtonBlock>
      </Form>
      <LeaveConfirmModal
        isSubModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
    </ModalLayout>
  );
});

export default SettingsUserModal;
